import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { apiRequest } from "./queryClient";

interface CustomerProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string;
  email: string;
  avatarUrl: string | null;
  walletBalance: string;
  kycStatus: string;
  pinSetupRequired?: boolean;
}

interface CustomerContextType {
  customer: CustomerProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshCustomer: () => Promise<void>;
  logout: () => Promise<void>;
  setCustomer: (customer: CustomerProfile | null) => void;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCustomer = useCallback(async () => {
    try {
      const response = await apiRequest("GET", "/api/customer/auth/me");
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
      } else {
        setCustomer(null);
      }
    } catch (error) {
      console.error("Failed to fetch customer:", error);
      setCustomer(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshCustomer = useCallback(async () => {
    await fetchCustomer();
  }, [fetchCustomer]);

  const logout = useCallback(async () => {
    try {
      await apiRequest("POST", "/api/customer/auth/logout");
      setCustomer(null);
      window.location.href = "/portal/login";
    } catch (error) {
      console.error("Logout failed:", error);
      // Still redirect even if API call fails
      setCustomer(null);
      window.location.href = "/portal/login";
    }
  }, []);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const value = {
    customer,
    isLoading,
    isAuthenticated: customer !== null,
    refreshCustomer,
    logout,
    setCustomer,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
}
